// packages/web/src/hooks/useEventStream.ts
import { useEffect, useRef, useState } from 'react';
import { useOnline } from '@/hooks/useOnline';
import { nextDelay } from '@/lib/backoff';

/**
 * Connection lifecycle status for a managed {@link EventSource}.
 * - `connecting`  — initial open in flight (no prior success yet)
 * - `open`        — stream is live
 * - `reconnecting`— transiently dropped; a reconnect is scheduled/in flight
 * - `error`       — escalated after repeated failures (>= {@link ERROR_ATTEMPT_THRESHOLD})
 */
export type EventStreamStatus = 'connecting' | 'open' | 'reconnecting' | 'error';

interface UseEventStreamOptions {
  /** Stream URL. `null` means "do not connect" (e.g. no conversation selected). */
  url: string | null;
  /** Invoked for every `message` event. Callers own their parsing. */
  onMessage: (ev: MessageEvent) => void;
  /** Invoked on each successful `open` (initial and every reconnect). */
  onOpen?: () => void;
  /** Forwarded to the EventSource constructor. Default true. */
  withCredentials?: boolean;
}

/**
 * Number of consecutive failed reconnect attempts after which the status
 * escalates from `reconnecting` to `error` (a hard, user-facing failure).
 */
export const ERROR_ATTEMPT_THRESHOLD = 4;

/**
 * Pure escalation decision for the CLOSED reconnect path: given the number of
 * consecutive failed attempts, decide whether the stream is still
 * `reconnecting` or has escalated to a hard `error`. Extracted as a pure helper
 * so the threshold logic is independently testable.
 */
export function reconnectStatus(
  attempt: number,
  threshold: number = ERROR_ATTEMPT_THRESHOLD
): 'reconnecting' | 'error' {
  return attempt >= threshold ? 'error' : 'reconnecting';
}

/**
 * Wall-clock backstop (ms) for a single `connect()` that opens (HTTP 200) but
 * never fires `onopen` — e.g. the body closes immediately, leaving the native
 * EventSource looping in CONNECTING forever. If `onopen` doesn't clear this
 * timer within the window, the status escalates to `error` so the user isn't
 * stuck on an indefinite spinner. Native CONNECTING retries do NOT call
 * `connect()`, so this deadline is intentionally not reset by them.
 */
const CONNECT_DEADLINE_MS = 60_000;

/**
 * Low-level resilient `EventSource` lifecycle hook.
 *
 * Owns a single EventSource and its reconnect policy so callers only supply
 * message parsing. Beyond native `EventSource` auto-retry it adds:
 * - **Manual capped backoff** once the socket reaches `CLOSED` (native retry
 *   only covers transient `CONNECTING` flaps; a `CLOSED` socket is dead and
 *   would otherwise never recover).
 * - **Foreground resume** on `visibilitychange` → `visible` (iOS/PWA kill
 *   background streams; the only reliable fix is force-close + immediate reopen).
 * - **Online resume** when connectivity returns (skip the remaining backoff wait).
 *
 * StrictMode/double-mount safe: exactly one EventSource at a time, all timers
 * and listeners are torn down on cleanup, and a `closedByCleanup` flag prevents
 * a late `onerror` from a torn-down socket scheduling a zombie reconnect.
 */
export function useEventStream(opts: UseEventStreamOptions): {
  status: EventStreamStatus;
  reconnectCount: number;
} {
  const { url, withCredentials = true } = opts;

  const [status, setStatus] = useState<EventStreamStatus>('connecting');
  const [reconnectCount, setReconnectCount] = useState(0);

  // Keep callbacks fresh without re-running the connection effect.
  const onMessageRef = useRef(opts.onMessage);
  onMessageRef.current = opts.onMessage;
  const onOpenRef = useRef(opts.onOpen);
  onOpenRef.current = opts.onOpen;

  const isOnline = useOnline();
  const wasOnlineRef = useRef(isOnline);

  // Connection state, all in refs so the [url] effect stays stable.
  const esRef = useRef<EventSource | null>(null);
  const attemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Per-connect wall-clock deadline. Backstops the CONNECTING-forever path
  // (stream opens then immediately closes its body) which never increments
  // attemptRef and so would otherwise never escalate.
  const connectDeadlineRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByCleanupRef = useRef(false);
  // Guards overlapping wake triggers (visibilitychange + online) from
  // force-opening two sockets back to back.
  const reconnectingRef = useRef(false);
  // Imperative force-reconnect handle, published by the connection effect so the
  // online-transition effect can trigger a wake without duplicating socket wiring.
  const forceReconnectRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    closedByCleanupRef.current = false;

    const clearReconnectTimer = (): void => {
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const clearConnectDeadline = (): void => {
      if (connectDeadlineRef.current !== null) {
        clearTimeout(connectDeadlineRef.current);
        connectDeadlineRef.current = null;
      }
    };

    const scheduleReconnect = (): void => {
      clearReconnectTimer();
      // attemptRef was already incremented by the failing onerror; the just-failed
      // attempt index is attemptRef - 1.
      const delay = nextDelay(attemptRef.current - 1);
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        if (closedByCleanupRef.current) return;
        connect();
      }, delay);
    };

    function connect(): void {
      if (url === null) return;
      // Close any prior socket — never run two EventSources for one stream.
      if (esRef.current !== null) {
        esRef.current.close();
        esRef.current = null;
      }
      clearReconnectTimer();
      reconnectingRef.current = false;

      // Arm the per-connect wall-clock deadline. Fires only if onopen never
      // clears it within the window (CONNECTING-forever path); native retries
      // do not re-enter connect(), so this is not reset by them.
      clearConnectDeadline();
      connectDeadlineRef.current = setTimeout(() => {
        connectDeadlineRef.current = null;
        if (closedByCleanupRef.current) return;
        setStatus('error');
      }, CONNECT_DEADLINE_MS);

      const es = new EventSource(url, { withCredentials });
      esRef.current = es;
      // Preserve an existing escalated/reconnecting status on a manual reopen;
      // only fall back to 'connecting' for a genuinely fresh connection.
      setStatus(prev => (prev === 'open' ? 'connecting' : prev));

      es.onopen = (): void => {
        if (closedByCleanupRef.current || esRef.current !== es) return;
        clearConnectDeadline();
        attemptRef.current = 0;
        reconnectingRef.current = false;
        setStatus('open');
        onOpenRef.current?.();
      };

      es.onmessage = (ev: MessageEvent): void => {
        if (closedByCleanupRef.current || esRef.current !== es) return;
        onMessageRef.current(ev);
      };

      es.onerror = (): void => {
        if (closedByCleanupRef.current || esRef.current !== es) return;
        if (es.readyState === EventSource.CLOSED) {
          // The socket is dead — native retry will NOT resume it. Take over
          // with a manual capped-backoff reconnect rather than leaving it dead.
          attemptRef.current += 1;
          setReconnectCount(c => c + 1);
          setStatus(reconnectStatus(attemptRef.current));
          scheduleReconnect();
        } else {
          // CONNECTING: native EventSource is already retrying. Leave it alone,
          // just surface the transient state (without un-escalating a hard error).
          setStatus(prev => (prev === 'error' ? prev : 'reconnecting'));
        }
      };
    }

    // Force an immediate reconnect, skipping any pending backoff. Debounced via
    // reconnectingRef so two near-simultaneous wakes don't double-open.
    //
    // `forceEvenIfOpen`: when true (foreground wake) we reconnect even if the
    // socket reports OPEN — iOS/PWA can leave a backgrounded stream silently
    // dead while still reporting OPEN, so a stale OPEN must not short-circuit.
    // When false (online wake) a genuinely live socket is left untouched.
    const forceReconnect = (forceEvenIfOpen: boolean): void => {
      if (closedByCleanupRef.current || url === null) return;
      if (reconnectingRef.current) return;
      if (!forceEvenIfOpen && esRef.current?.readyState === EventSource.OPEN) return;
      reconnectingRef.current = true;
      attemptRef.current = 0;
      connect();
    };
    forceReconnectRef.current = (): void => {
      forceReconnect(false);
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        // iOS/PWA load-bearing fix: a backgrounded stream may be silently dead
        // while still reporting OPEN. Force a fresh socket on foreground.
        forceReconnect(true);
      }
    };

    connect();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return (): void => {
      closedByCleanupRef.current = true;
      clearReconnectTimer();
      clearConnectDeadline();
      reconnectingRef.current = false;
      forceReconnectRef.current = (): void => undefined;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (esRef.current !== null) {
        esRef.current.close();
        esRef.current = null;
      }
    };
    // Reconnect callbacks are read from refs; only the URL/credentials drive
    // a teardown + reconnect.
  }, [url, withCredentials]);

  // Online transition (false → true): skip the remaining backoff and reconnect now.
  useEffect(() => {
    const wasOnline = wasOnlineRef.current;
    wasOnlineRef.current = isOnline;
    if (!wasOnline && isOnline) {
      forceReconnectRef.current();
    }
  }, [isOnline]);

  return { status, reconnectCount };
}
